import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getWhiteboards, createWhiteboard, deleteWhiteboard, Whiteboard as WhiteboardType } from '../../services/whiteboardService';
import PageHeader from '../common/PageHeader';
import './Whiteboard.css';

interface WhiteboardProps {
  onToggleSidebar: () => void;
}

const Whiteboard: React.FC<WhiteboardProps> = ({ onToggleSidebar }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [whiteboards, setWhiteboards] = useState<WhiteboardType[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [newBoardName, setNewBoardName] = useState('');

  useEffect(() => {
    loadWhiteboards();
  }, []);

  const loadWhiteboards = async () => {
    setIsLoading(true);
    try {
      const boards = await getWhiteboards();
      setWhiteboards(boards);
    } catch (error) {
      console.error('Failed to load whiteboards:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateBoard = async () => {
    if (!newBoardName.trim()) {
      alert('Please enter a board name');
      return;
    }

    setIsCreating(true);
    try {
      const board = await createWhiteboard(newBoardName.trim());
      navigate(`/whiteboard/${board.id}`);
    } catch (error) {
      console.error('Failed to create board:', error);
      alert('Failed to create board');
    } finally {
      setIsCreating(false);
      setNewBoardName('');
    }
  };

  const handleDeleteBoard = async (id: number, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!window.confirm('Are you sure you want to delete this board?')) return;

    try {
      await deleteWhiteboard(id);
      loadWhiteboards();
    } catch (error) {
      console.error('Failed to delete board:', error);
      alert('Failed to delete board');
    }
  };

  const handleBoardClick = (id: number) => {
    navigate(`/whiteboard/${id}`);
  };

  if (!user) {
    return (
      <div className="whiteboard-container">
        <PageHeader
          onToggleSidebar={onToggleSidebar}
          centerContent={<h1 className="page-header-center-title">/whiteboard</h1>}
        />
        <div className="whiteboard-empty-state">
          <p>Please sign in to access whiteboards</p>
        </div>
      </div>
    );
  }

  return (
    <div className="whiteboard-container">
      <PageHeader
        onToggleSidebar={onToggleSidebar}
        centerContent={<h1 className="page-header-center-title">/whiteboard</h1>}
      />
      
      <div className="whiteboard-content">
        <div className="whiteboard-create-section">
          <input
            type="text"
            className="whiteboard-create-input"
            placeholder="Enter board name..."
            value={newBoardName}
            onChange={(e) => setNewBoardName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleCreateBoard();
              }
            }}
            disabled={isCreating}
          />
          <button
            className="whiteboard-create-btn"
            onClick={handleCreateBoard}
            disabled={isCreating || !newBoardName.trim()}
          >
            {isCreating ? 'Creating...' : 'Create New Board'}
          </button>
        </div>

        {isLoading ? (
          <div className="whiteboard-loading">Loading boards...</div>
        ) : whiteboards.length === 0 ? (
          <div className="whiteboard-empty-state">
            <p>No boards yet. Create your first board above!</p>
          </div>
        ) : (
          <div className="whiteboard-list">
            {whiteboards.map((board) => (
              <div
                key={board.id}
                className="whiteboard-card"
                onClick={() => handleBoardClick(board.id)}
              >
                <div className="whiteboard-card-header">
                  <h3 className="whiteboard-card-title">{board.name}</h3>
                  <button
                    className="whiteboard-card-delete-btn"
                    onClick={(e) => handleDeleteBoard(board.id, e)}
                    title="Delete board"
                  >
                    ×
                  </button>
                </div>
                <div className="whiteboard-card-meta">
                  <span className="whiteboard-card-date">
                    Created: {new Date(board.created_at).toLocaleDateString()}
                  </span>
                  <span className="whiteboard-card-objects">
                    {board.objects?.length || 0} object{(board.objects?.length || 0) !== 1 ? 's' : ''}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Whiteboard;

